import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import PublicIcon from "@mui/icons-material/Public";
import PersonIcon from "@mui/icons-material/Person";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import BoardColumn from "../components/tasksntickets/BoardColumn";
import DepartmentFilterChips from "../components/tasksntickets/DepartmentFilterChips";
import NewTicketDialog from "../components/tasksntickets/NewTicketDialog";
import ProjectsBoard from "../components/tasksntickets/ProjectsBoard";
import {
  COLORS,
  RADIUS,
  STATUS_COLUMNS,
} from "../components/tasksntickets/ticketTheme";
import { useTickets } from "../store/useTickets";
import { api } from "../utils/axios-instance";
import useAuth from "../stores/authStore";
import { isStarterPlan } from "../utils/planAccess";

export default function TicketsBoard() {
  const [visibility, setVisibility] = useState("public"); // "public" | "personal"

  // Starter-tier orgs don't get the "Personal" (private-to-me) ticket
  // visibility — same STARTER_PLAN_CODES gate used for User Management etc.
  const [planCode, setPlanCode] = useState("");
  const personalDisabled = isStarterPlan(planCode);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/payments/current/");
        if (!cancelled) setPlanCode(res.data?.planCode || "");
      } catch (err) {
        console.error("Failed to load plan info", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // If the org is (or becomes) Starter tier while "Personal" is selected,
  // fall back to "Public" so the disabled option is never left active.
  useEffect(() => {
    if (personalDisabled && visibility === "personal") setVisibility("public");
  }, [personalDisabled, visibility]);
  const [boardView, setBoardView] = useState("tickets"); // "tickets" | "projects"
  const [selectedDepartments, setSelectedDepartments] = useState([]); // department ids, [] = all
  const [search, setSearch] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create"); // "create" | "edit"
  const [activeTicket, setActiveTicket] = useState(null);

  // Only relevant when boardView === "projects" — toggles the inline
  // "+ New project" creation row (see ProjectsBoard/NewProjectRow), the
  // same way the reference's "+ New project" button does.
  const [creatingProject, setCreatingProject] = useState(false);

  // Department filtering happens client-side (see filteredBoard below), not
  // as a query param — that's what lets the filter chips keep showing each
  // department's true total even while other departments are selected.
  const {
    board,
    departments,
    loading,
    createTicket,
    updateTicket,
    deleteTicket,
    moveTicket,
    assignTicket,
    refetch,
  } = useTickets({ visibility, search });

  // Same source of truth as SideBar.jsx / TeamManagementTable.jsx: the
  // logged-in user's profile lives in the authStore, keyed off Firebase
  // auth + hydrated from secureLocalStorage.
  const profileData = useAuth((s) => s.profileData);
  const currentUser = useMemo(() => {
    if (!profileData?.id) return null;
    const name =
      `${profileData.name || ""} ${profileData.last_name || ""}`.trim();
    return {
      id: profileData.id,
      name: name || profileData.username || profileData.email || "You",
      email: profileData.email || "",
      profilePictureUrl: profileData.profilePictureUrl || "",
    };
  }, [profileData]);

  // Full org roster — used by the ticket dialog's Assignees picker AND by
  // ProjectsBoard's per-task assignee picker (projects aren't department
  // scoped, so ProjectsBoard uses the whole roster rather than narrowing
  // by department the way NewTicketDialog does for tickets).
  const [orgUsers, setOrgUsers] = useState([]);
  const [orgUsersLoading, setOrgUsersLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setOrgUsersLoading(true);
      try {
        const res = await api.get("/users/list_users");
        const onboarded = (res.data || []).filter(
          (u) => u.status === "onboarded" && u.flags?.is_approved === true
        );
        if (!cancelled) setOrgUsers(onboarded);
      } catch (err) {
        console.error("Failed to load org users", err);
      } finally {
        if (!cancelled) setOrgUsersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  // Counts always reflect the full (visibility+search-filtered, but NOT
  // department-filtered) board, so every chip — selected or not — shows
  // its real total.
  const departmentCounts = useMemo(() => {
    const counts = { all: 0 };
    Object.values(board || {}).forEach((tickets) => {
      tickets.forEach((t) => {
        counts.all += 1;
        const deptId = t.department_id || "unknown";
        counts[deptId] = (counts[deptId] || 0) + 1;
      });
    });
    return counts;
  }, [board]);

  // The board actually rendered into columns — narrowed to the union of
  // selected departments, client-side.
  const filteredBoard = useMemo(() => {
    if (selectedDepartments.length === 0) return board;

    const selectedSet = new Set(selectedDepartments.map(String));
    const next = {};
    Object.keys(board || {}).forEach((statusKey) => {
      next[statusKey] = (board[statusKey] || []).filter((t) =>
        selectedSet.has(String(t.department_id))
      );
    });
    return next;
  }, [board, selectedDepartments]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    const ticketId = active.id;
    const targetStatus = over.data?.current?.status;
    if (!targetStatus) return;

    const sourceStatus = active.data?.current?.status;
    if (sourceStatus === targetStatus) return;

    // Optimistic update handled inside moveTicket (hook updates local cache first)
    moveTicket(ticketId, targetStatus);
  };

  const toggleDepartment = (deptId) => {
    setSelectedDepartments((prev) =>
      prev.includes(deptId)
        ? prev.filter((d) => d !== deptId)
        : [...prev, deptId]
    );
  };

  const openCreateDialog = () => {
    setActiveTicket(null);
    setDialogMode("create");
    setDialogOpen(true);
  };

  const openEditDialog = (ticket) => {
    setActiveTicket(ticket);
    setDialogMode("edit");
    setDialogOpen(true);
  };

  const handleNewClick = () => {
    if (boardView === "tickets") {
      openCreateDialog();
    } else {
      setCreatingProject(true);
    }
  };

  return (
    <Box
      sx={{ p: { xs: 2, md: 3 }, bgcolor: COLORS.pageBg, minHeight: "100%" }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr auto 1fr" },
          alignItems: "center",
          columnGap: 2,
          rowGap: 1.5,
          mb: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h5"
            fontWeight={800}
            noWrap
            sx={{ color: COLORS.textPrimary }}
          >
            Tasks &amp; Tickets
          </Typography>
          <Typography
            variant="body2"
            noWrap
            sx={{ color: COLORS.textSecondary }}
          >
            {boardView === "tickets"
              ? "Track work across departments — assign, prioritize, and ship."
              : "Plan and track your work across projects."}
          </Typography>
        </Box>

        <Box sx={{ justifySelf: { xs: "start", md: "center" } }}>
          <PillToggle
            value={visibility}
            onChange={setVisibility}
            options={[
              {
                value: "public",
                label: "Public",
                icon: <PublicIcon sx={{ fontSize: 15 }} />,
              },
              {
                value: "personal",
                label: "Personal",
                icon: <PersonIcon sx={{ fontSize: 15 }} />,
                disabled: personalDisabled,
              },
            ]}
            activeColor={COLORS.accent}
          />
        </Box>

        <Stack
          direction="row"
          flexWrap="wrap"
          useFlexGap
          spacing={1.5}
          rowGap={1}
          alignItems="center"
          sx={{ justifySelf: { xs: "start", md: "end" } }}
        >
          <PillToggle
            value={boardView}
            onChange={setBoardView}
            options={[
              { value: "tickets", label: "Tickets" },
              // { value: "projects", label: "Projects" },
              {
                value: "projects",
                label: "Projects",
                disabled: true,
              },
            ]}
            activeColor={COLORS.segmentActiveBg}
          />

          <Box
            component="button"
            type="button"
            onClick={handleNewClick}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              bgcolor: COLORS.accent,
              color: "#fff",
              border: "none",
              borderRadius: `${RADIUS.field}px`,
              px: 2,
              py: 1,
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
              "&:hover": { bgcolor: "#5B49D6" },
            }}
          >
            <AddIcon sx={{ fontSize: 18 }} />
            {boardView === "tickets" ? "New ticket" : "New project"}
          </Box>
        </Stack>
      </Box>

      {boardView === "tickets" ? (
        <>
          <TextField
            fullWidth
            size="small"
            placeholder="Search tickets, descriptions, assignees..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              my: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: `${RADIUS.field}px`,
                bgcolor: COLORS.cardBg,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: COLORS.textDisabled }}
                  />
                </InputAdornment>
              ),
            }}
          />

          <DepartmentFilterChips
            departments={departments}
            counts={departmentCounts}
            selected={selectedDepartments}
            onToggle={toggleDepartment}
            onClear={() => setSelectedDepartments([])}
          />

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <Stack
              direction="row"
              spacing={2}
              mt={3}
              sx={{
                overflowX: "auto",
                pb: 1,
                "&::-webkit-scrollbar": { height: 6 },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: COLORS.border,
                  borderRadius: `${RADIUS.pill}px`,
                },
              }}
            >
              {STATUS_COLUMNS.map((col) => (
                <BoardColumn
                  key={col.key}
                  status={col.key}
                  label={col.label}
                  color={col.color}
                  tickets={filteredBoard?.[col.key] || []}
                  loading={loading}
                  departments={departments}
                  onQuickAdd={openCreateDialog}
                  onOpenTicket={openEditDialog}
                  onDeleteTicket={(ticketId) => deleteTicket(ticketId)}
                />
              ))}
            </Stack>
          </DndContext>

          <NewTicketDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            mode={dialogMode}
            ticket={activeTicket}
            departments={departments}
            visibilityContext={visibility}
            orgUsers={orgUsers}
            orgUsersLoading={orgUsersLoading}
            currentUser={currentUser}
            onSubmit={async (payload) => {
              await createTicket(payload);
              refetch();
            }}
            onUpdate={(ticketId, changes) => updateTicket(ticketId, changes)}
            onMove={(ticketId, status) => moveTicket(ticketId, status)}
            onAssign={(ticketId, assigneeUid) =>
              assignTicket(ticketId, assigneeUid)
            }
            onDelete={(ticketId) => deleteTicket(ticketId)}
          />
        </>
      ) : (
        <Box sx={{ mt: 2 }}>
          <ProjectsBoard
            visibility={visibility}
            orgUsers={orgUsers}
            creatingProject={creatingProject}
            onCreatingProjectChange={setCreatingProject}
          />
        </Box>
      )}
    </Box>
  );
}

/** Rounded pill segmented control — the "Public / Personal" and
 * "Tickets / Projects" switches in the reference design. */
function PillToggle({ value, onChange, options, activeColor }) {
  return (
    <Stack
      direction="row"
      sx={{
        bgcolor: COLORS.pageBg,
        border: "1px solid",
        borderColor: COLORS.border,
        borderRadius: `${RADIUS.pill}px`,
        p: 0.375,
      }}
    >
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <Box
            key={opt.value}
            component="button"
            type="button"
            disabled={opt.disabled}
            onClick={() => !opt.disabled && onChange(opt.value)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              border: "none",
              borderRadius: `${RADIUS.pill}px`,
              px: 1.75,
              py: 0.625,
              fontSize: 13,
              fontWeight: 700,
              cursor: opt.disabled ? "not-allowed" : "pointer",
              bgcolor: active ? activeColor : "transparent",
              color: active
                ? "#fff"
                : opt.disabled
                  ? COLORS.textDisabled
                  : COLORS.textPrimary,
              transition: "all 120ms ease",
            }}
          >
            {opt.icon}
            {opt.label}
          </Box>
        );
      })}
    </Stack>
  );
}
